import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { dateFilters } from "../utils/dateFilters";
import apiService from "../../../../core/services/apiService";
import { useDispatch } from "react-redux";
import TableSkeleton from "../../../../core/components/skeleton/TableSkeleton";

export default function StatModal({
  isOpen,
  onClose,
  type,
  allItems = [],
  chartData = [],
}) {
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [dateFilter, setDateFilter] = useState("This Month"); // 👈 added
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "created_on",
    direction: "desc",
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const modalRef = React.useRef(null);
  const dispatch = useDispatch();

  // Reset pagination when dataset type changes
  useEffect(() => {
    setPage(1);
    setDateFilter("This Month");
    setCustomStart("");
    setCustomEnd("");

    // --- Fetch transactions based on dateFilter ---
    const fetchData = async () => {
      if (!["transactions", "stockIn", "stockOut", "topItems"].includes(type))
        return;

      setLoading(true);
      try {
        const data = await apiService.get(dispatch, "inventoryLogs");
        setTransactions(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false); // stop loading
      }
    };

    fetchData();
  }, [type, chartData, dispatch]);

  useEffect(() => {
    const calculateRows = () => {
      if (modalRef.current) {
        const availableHeight = modalRef.current.clientHeight * 0.7;
        const rowHeight = 36;
        const headerHeight = 36;
        const newItemsPerPage = Math.max(
          1,
          Math.floor((availableHeight - headerHeight) / rowHeight)
        );
        setItemsPerPage(newItemsPerPage);
      }
    };

    if (isOpen) {
      setTimeout(calculateRows, 100);
    }

    window.addEventListener("resize", calculateRows);
    return () => window.removeEventListener("resize", calculateRows);
  }, [isOpen]);

  // Close modal with ESC key
  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // Prevent background scroll
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpen]);

  if (!isOpen) return null;

  // --- Determine dataset based on type ---
  let rawData = [];
  let title = "";

  switch (type) {
    case "items":
      if (chartData.type) {
        switch (chartData.type) {
          case "composition":
            const categoryName = chartData?.data?.name || ""; // e.g. "Supply"
            title = `🧩 ${categoryName} Items`;

            rawData = allItems
              .filter((i) => (i.category || "Uncategorized") === categoryName)
              .map((i) => ({
                id: i._id,
                name: i.name,
                category: i.category || "Uncategorized",
                quantity: i.quantity,
                reorderLevel: i.reorderLevel,
                unit: i.unit || "-",
                expiryDate: i.expiryDate,
                created_on: i.created_on,
              }));
            break;

          case "stockHealth":
            const clickedSegment = chartData?.data?.name || "";
            if (clickedSegment === "Low Stock") {
              title = "❤️ Low Stock Items";
              rawData = allItems.filter((i) => i.quantity <= i.reorderLevel);
            } else if (clickedSegment === "Healthy") {
              title = "💚 Healthy Stock Items";
              rawData = allItems.filter((i) => i.quantity > i.reorderLevel);
            } else {
              title = "📦 Stock Health Overview";
              rawData = allItems;
            }
            break;

          case "expiry":
            const range = chartData?.data?.range || "";
            const now = new Date();
            const addDays = (days) => {
              const d = new Date(now);
              d.setDate(d.getDate() + days);
              return d;
            };

            let start = now;
            let end = addDays(99999); // default huge upper bound

            if (range === "Expired") {
              start = new Date(0); // way in the past
              end = now; // up to today
              title = "⚠️ Expired Items";
            } else if (range === "0–30 days") {
              start = now;
              end = addDays(30);
              title = "⏳ Expiring within 30 days";
            } else if (range === "31–60 days") {
              start = addDays(31);
              end = addDays(60);
              title = "📆 Expiring in 31–60 days";
            } else if (range === "61–90 days") {
              start = addDays(61);
              end = addDays(90);
              title = "🗓️ Expiring in 61–90 days";
            } else {
              title = "⏳ Items Approaching Expiry";
            }

            rawData = allItems.filter((i) => {
              if (!i.expiryDate) return false;
              const exp = new Date(i.expiryDate);
              return exp >= start && exp <= end;
            });
            break;

          default:
            title = "📦 All Inventory Items";
            rawData = allItems;
            break;
        }
      } else {
        title = "📦 All Inventory Items";
        rawData = allItems;
      }
      break;

    case "stockIn":
      rawData = transactions.filter((t) => t.type === "Stock in");
      title = `📈 Stock In Transactions${
        dateFilter !== "All" ? ` — ${dateFilter}` : ""
      }`;
      break;

    case "stockOut":
      rawData = transactions.filter((t) => t.type === "Stock out");
      title = `📉 Stock Out Transactions${
        dateFilter !== "All" ? ` — ${dateFilter}` : ""
      }`;
      break;

    case "transactions":
      rawData = transactions;
      title = `🔄 All Transactions${
        dateFilter !== "All" ? ` — ${dateFilter}` : ""
      }`;
      break;

    case "topItems":
      const clickedItemName = chartData?.data?.name || "";
      rawData = transactions.filter((t) => t.item?.name === clickedItemName);
      title = `🏆 ${clickedItemName} — Stock Movements`;
      break;

    default:
      title = "📊 Data Overview";
  }

  if (dateFilter === "Custom Range" && customStart && customEnd) {
    const startLabel = new Date(customStart).toLocaleDateString();
    const endLabel = new Date(customEnd).toLocaleDateString();
    title = `${title.replace(/ — .*/, "")} — ${startLabel} → ${endLabel}`;
  }

  const parsePHDate = (str, endOfDay = false) => {
    const [y, m, d] = str.split("-").map(Number);
    const date = new Date(y, m - 1, d);

    if (endOfDay) {
      date.setHours(23, 59, 59, 999); // FULL day
    }

    return date;
  };

  const applyDateFilter = (data) => {
    if (!["transactions", "stockIn", "stockOut", "topItems"].includes(type))
      return data;

    if (dateFilter === "All") return data;

    let start = null;
    let end = null;

    if (dateFilter === "Custom Range" && customStart && customEnd) {
      start = parsePHDate(customStart);
      end = parsePHDate(customEnd, true);
    } else if (dateFilters[dateFilter]) {
      [start, end] = dateFilters[dateFilter]();
    }

    return data.filter((t) => {
      const created = new Date(t.created_on);
      return !start || (created >= start && created <= end);
    });
  };

  const filteredByDate = applyDateFilter(rawData);

  // --- Sorting Logic ---
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedData = [...filteredByDate].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    let valA, valB;
    if (type === "items") {
      valA = a[key] ?? "";
      valB = b[key] ?? "";
    } else {
      // handle nested fields
      if (key === "item.name") {
        valA = a.item?.name ?? "";
        valB = b.item?.name ?? "";
      } else {
        valA = a[key] ?? "";
        valB = b[key] ?? "";
      }
    }

    // Convert to comparable types
    if (typeof valA === "string") valA = valA.toLowerCase();
    if (typeof valB === "string") valB = valB.toLowerCase();

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });

  // --- Enhanced Search ---
  const filteredData = sortedData.filter((row) => {
    const term = search.toLowerCase();

    if (type === "items") {
      return (
        row.id?.toLowerCase().includes(term) ||
        row.name?.toLowerCase().includes(term) ||
        row.category?.toLowerCase().includes(term) ||
        row.unit?.toLowerCase().includes(term) ||
        (row.expiryDate &&
          new Date(row.expiryDate)
            .toLocaleDateString()
            .toLowerCase()
            .includes(term))
      );
    } else {
      return (
        row.id?.toLowerCase().includes(term) ||
        row.item?.name?.toLowerCase().includes(term) ||
        row.type?.toLowerCase().includes(term) ||
        row.reason?.toLowerCase().includes(term) ||
        (row.created_on &&
          new Date(row.created_on)
            .toLocaleString()
            .toLowerCase()
            .includes(term))
      );
    }
  });

  // --- Pagination logic unchanged ---
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const start = (page - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(start, start + itemsPerPage);

  // console.log(paginatedData);

  // --- Reset on close ---
  const handleClose = () => {
    setSearch("");
    setPage(1);
    setSortConfig({ key: "created_on", direction: "desc" });
    onClose();
  };

  // --- Small arrow helper ---
  const SortArrow = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return (
      <span className="ml-1 text-xs">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b px-6 py-4 bg-gray-50">
          {/* Top row: Title + Close button */}
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Bottom row: Search + Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search..."
              className="px-3 py-1.5 border rounded-lg text-sm w-56 focus:outline-none focus:ring focus:ring-blue-200"
            />

            {/* Date filters (only for certain types) */}
            {["transactions", "stockIn", "stockOut", "topItems"].includes(
              type
            ) && (
              <>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                >
                  {Object.keys(dateFilters).map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>

                {dateFilter === "Custom Range" && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <span>–</span>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6  min-h-[65vh] max-h-[65vh]  overflow-y-auto table-container">
          {loading ? (
            <TableSkeleton type={type} rows={itemsPerPage} />
          ) : paginatedData.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 text-left select-none">
                  {type === "items" ? (
                    <>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("name")}
                      >
                        Item <SortArrow columnKey="name" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("category")}
                      >
                        Category <SortArrow columnKey="category" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("quantity")}
                      >
                        Quantity <SortArrow columnKey="quantity" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("reorderLevel")}
                      >
                        Reorder Level <SortArrow columnKey="reorderLevel" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("expiryDate")}
                      >
                        Expiry <SortArrow columnKey="expiryDate" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("created_on")}
                      >
                        Created <SortArrow columnKey="created_on" />
                      </th>
                    </>
                  ) : (
                    <>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("item.name")}
                      >
                        Item <SortArrow columnKey="item.name" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("type")}
                      >
                        Type <SortArrow columnKey="type" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("quantity")}
                      >
                        Quantity <SortArrow columnKey="quantity" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("reason")}
                      >
                        Reason <SortArrow columnKey="reason" />
                      </th>
                      <th
                        className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                        onClick={() => handleSort("created_on")}
                      >
                        Created <SortArrow columnKey="created_on" />
                      </th>
                      <th className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer">
                        Created by
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition">
                    {type === "items" ? (
                      <>
                        <td
                          className="px-4 py-2 border-b  text-blue-600 cursor-pointer"
                          onClick={() =>
                            window.open(
                              `/form/inventory/view/${row._id ?? row.id}`,
                              "_blank"
                            )
                          }
                        >
                          {row.name}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.category || "-"}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.quantity} {row.unit || ""}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.reorderLevel}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.expiryDate
                            ? new Date(row.expiryDate).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {new Date(row.created_on).toLocaleString()}
                        </td>
                      </>
                    ) : (
                      <>
                        <td
                          className="px-4 py-2 border-b text-blue-600 cursor-pointer"
                          onClick={() =>
                            window.open(
                              `/form/inventory/view/${row.item._id}`,
                              "_blank"
                            )
                          }
                        >
                          {row.item?.name || "Unknown"}
                        </td>
                        <td
                          className={`px-4 py-2 border-b font-medium ${
                            row.type === "Stock in"
                              ? "text-green-600"
                              : row.type === "Stock out"
                              ? "text-red-600"
                              : "text-gray-700"
                          }`}
                        >
                          {row.type}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.quantity} {row.item?.unit || ""}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.reason}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {new Date(row.created_on).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 border-b text-gray-700">
                          {row.created_by.name}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center py-6">No records found.</p>
          )}
        </div>

        {/* Footer with total + pagination */}
        <div className="flex justify-between items-center px-6 py-3 border-t bg-gray-50 text-sm">
          <span>
            🔢 Total: <b>{filteredData.length}</b> records
          </span>

          {filteredData.length > itemsPerPage && (
            <div className="flex items-center gap-3">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
